import { useEffect, useRef } from "react";
import useDeviceType from "../../utils/screen";
import s from "./style.module.scss";
import { videoSrc, thumbnailSrc } from "utils/media";

interface VideoContainerProps {
  videoId?: string;
  className?: string;
  assetType: string;
  r2BucketUrl?: string;
}

export const VideoContainer = ({
  videoId,
  className,
  assetType,
  r2BucketUrl,
}: VideoContainerProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { isMobile } = useDeviceType();

  useEffect(() => {
    if (assetType !== "iframe" || !iframeRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && iframeRef.current) {
            const src = iframeRef.current.getAttribute("data-src");
            if (src) {
              iframeRef.current.src = src;
              iframeRef.current.removeAttribute("data-src");
              observer.unobserve(iframeRef.current);
            }
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: isMobile ? "400px" : "200px",
      }
    );

    observer.observe(iframeRef.current);

    return () => {
      if (iframeRef.current) observer.unobserve(iframeRef.current);
    };
  }, [videoId, assetType, isMobile]);

  if (assetType === "iframe" && videoId) {
    const thumbnailUrl = `https://customer-yp8wps06c5vlapon.cloudflarestream.com/${videoId}/thumbnails/thumbnail.jpg`;
    const iframeSrc = `https://iframe.videodelivery.net/${videoId}/iframe?autoplay=1&muted=1&controls=0&loop=1&poster=${thumbnailUrl}`;

    return (
      <div className={`${s.videoContainer} ${className || ""}`}>
        <iframe
          ref={iframeRef}
          data-src={iframeSrc}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className={s.iframe}
          style={{ border: 0 }}
          loading="lazy"
        />
      </div>
    );
  }

  if (assetType === "migrate" && videoId) {
    return (
      <div className={`${s.videoContainer} ${className || ""}`}>
        <video
          playsInline
          autoPlay
          muted
          loop
          poster={thumbnailSrc(videoId)}
          style={{ width: "100%", height: "auto" }}
        >
          <source
            src={videoSrc(`/streamsMigration/videos/${videoId}`)}
            type="video/mp4"
          />
        </video>
      </div>
    );
  }

  if (assetType === "video" && r2BucketUrl) {
    const poster = videoId
      ? `https://customer-yp8wps06c5vlapon.cloudflarestream.com/${videoId}/thumbnails/thumbnail.jpg`
      : undefined;

    return (
      <div className={`${s.videoContainer} ${className || ""}`}>
        <video
          playsInline
          autoPlay
          muted
          loop
          poster={poster}
          style={{ width: "100%", height: "auto" }}
        >
          <source src={videoSrc(r2BucketUrl)} type="video/mp4" />
        </video>
      </div>
    );
  }

  return null;
};
